import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import {
  CreditCard,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  PauseCircle,
  Clock,
  ExternalLink,
  Info,
  AlertCircle,
  TrendingUp,
  FileText,
  Shield,
  Zap,
  ChevronRight
} from "lucide-react";

// Configuración anti-zoom OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA FACTURACIÓN
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
  solid: "#0ea5e9",
  light: "#0ea5e910",
  description: "Gestión de suscripción y pagos",
});

const ESTADOS_SUSCRIPCION = {
  authorized: { 
    color: "#059669", 
    label: "Activa", 
    icon: <CheckCircle size={14} />,
    bgColor: "#ecfdf5"
  },
  paused: { 
    color: "#d97706", 
    label: "Pausada", 
    icon: <PauseCircle size={14} />,
    bgColor: "#fffbeb"
  },
  cancelled: { 
    color: "#dc2626", 
    label: "Cancelada", 
    icon: <XCircle size={14} />,
    bgColor: "#fef2f2"
  },
  pending: { 
    color: "#2563eb", 
    label: "Pendiente", 
    icon: <Clock size={14} />,
    bgColor: "#eff6ff"
  },
  default: { 
    color: "#6b7280", 
    label: "Desconocido", 
    icon: <Clock size={14} />,
    bgColor: "#f9fafb"
  }
};

function Facturacion({ constructora, usuario }) {
  const [suscripcion, setSuscripcion] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    cargarDatosFacturacion();
    return () => window.removeEventListener('resize', handleResize);
  }, [usuario]);

  const cargarDatosFacturacion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: suscripciones, error: subError } = await supabase
        .from('suscripciones')
        .select('*')
        .eq('email', usuario?.email)
        .order('created_at', { ascending: false })
        .limit(1);

      if (subError) throw subError;

      const { data: pagosUsuario, error: payError } = await supabase
        .from('pagos')
        .select('*')
        .eq('email', usuario?.email)
        .order('fecha_pago', { ascending: false });

      if (payError) throw payError;

      setSuscripcion(suscripciones?.[0] || null);
      setPagos(pagosUsuario || []);
      
      // Debug: Mostrar información de la suscripción
      if (suscripciones?.[0]) {
        console.log('📊 Datos de suscripción:', {
          id: suscripciones[0].mp_subscription_id,
          estado: suscripciones[0].estado,
          fecha_inicio: suscripciones[0].fecha_inicio,
          fecha_ultimo_pago: suscripciones[0].fecha_ultimo_pago,
          fecha_proximo_pago: suscripciones[0].fecha_proximo_pago,
          monto: suscripciones[0].monto,
          moneda: suscripciones[0].moneda,
          plan: suscripciones[0].plan,
          trial_days: suscripciones[0].trial_days,
          discount_code: suscripciones[0].discount_code,
          plan_original_amount: suscripciones[0].plan_original_amount,
          detalles: suscripciones[0].detalles
        });
      }
      
    } catch (err) {
      console.error('Error cargando datos de facturación:', err);
      setError('Error al cargar los datos de facturación');
    } finally {
      setLoading(false);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "No disponible";
    return new Date(fecha).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatearMonto = (monto) => {
    if (!monto) return "$0";
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(monto);
  };

  const obtenerEstadoSuscripcion = (estado) => {
    return ESTADOS_SUSCRIPCION[estado] || ESTADOS_SUSCRIPCION.default;
  };

  const calcularProximoPago = () => {
    // 1. Primero intentar usar la fecha que viene de Mercado Pago
    if (suscripcion?.fecha_proximo_pago) {
      return new Date(suscripcion.fecha_proximo_pago);
    }
    
    // 2. Si no hay fecha de próximo pago, calcular basándose en el último pago
    if (suscripcion?.fecha_ultimo_pago) {
      const ultimoPago = new Date(suscripcion.fecha_ultimo_pago);
      const proximoPago = new Date(ultimoPago);
      proximoPago.setMonth(proximoPago.getMonth() + 1);
      return proximoPago;
    }
    
    // 3. Si no hay último pago, calcular basándose en la fecha de inicio
    if (suscripcion?.fecha_inicio) {
      const fechaInicio = new Date(suscripcion.fecha_inicio);
      const proximoPago = new Date(fechaInicio);
      proximoPago.setMonth(proximoPago.getMonth() + 1);
      return proximoPago;
    }
    
    return null;
  };

  const totalPagado = pagos
    .filter(p => p.estado === 'approved')
    .reduce((sum, p) => sum + (p.monto_bruto || 0), 0);

  const colorVista = getVistaColor();

  if (loading) {
    return (
      <div style={{
        padding: isMobile ? "20px" : "40px",
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            animation: "spin 1s linear infinite",
            marginBottom: "16px",
            width: "32px",
            height: "32px",
            border: "3px solid #e2e8f0",
            borderTop: "3px solid " + colorVista.solid,
            borderRadius: "50%",
            margin: "0 auto 16px auto"
          }}></div>
          <p style={{ color: "#64748b", margin: 0 }}>Cargando información...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: isMobile ? "20px" : "40px",
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ textAlign: "center" }}>
          <AlertCircle size={48} color="#ef4444" style={{ marginBottom: "16px" }} />
          <p style={{ color: "#374151", marginBottom: "16px" }}>{error}</p>
          <button
            onClick={cargarDatosFacturacion}
            style={{
              background: colorVista.gradient,
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '16px 12px 120px 12px' : '20px',
      maxWidth: '100%',
      margin: '0 auto',
      background: '#f8fafc',
      minHeight: '100vh',
      animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Header unificado */}
      <div className="header-section" style={{
        marginBottom: '24px',
        animation: 'slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '16px',
          minHeight: '56px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '200px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(100, 116, 139, 0.25)',
              flexShrink: 0
            }}>
              <CreditCard size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '700',
                color: '#0f172a',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Facturación
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                margin: 0,
                fontWeight: '400',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Gestión de suscripción y pagos
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: "24px"
      }}>
        {/* Panel Principal */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Estado de Suscripción */}
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #e2e8f0"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "20px"
            }}>
              <h2 style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "700",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <Shield size={20} color={colorVista.solid} />
                Estado de Suscripción
              </h2>
              {suscripcion && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "600",
                  backgroundColor: obtenerEstadoSuscripcion(suscripcion.estado).bgColor,
                  color: obtenerEstadoSuscripcion(suscripcion.estado).color
                }}>
                  {obtenerEstadoSuscripcion(suscripcion.estado).icon}
                  {obtenerEstadoSuscripcion(suscripcion.estado).label}
                </div>
              )}
            </div>

            {suscripcion ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                gap: "16px"
              }}>
                <div style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "6px"
                  }}>
                    <DollarSign size={14} color="#64748b" />
                    <span style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>
                      Costo Mensual
                    </span>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#1e293b"
                  }}>
                    {formatearMonto(suscripcion.monto)}
                  </p>
                </div>

                <div style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "6px"
                  }}>
                    <Calendar size={14} color="#64748b" />
                    <span style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>
                      Próximo Pago
                    </span>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#1e293b"
                  }}>
                    {formatearFecha(calcularProximoPago())}
                  </p>
                  {suscripcion?.fecha_proximo_pago && (
                    <p style={{
                      margin: "2px 0 0 0",
                      fontSize: "10px",
                      color: "#10b981",
                      fontWeight: "500"
                    }}>
                      Desde MP
                    </p>
                  )}
                  {!suscripcion?.fecha_proximo_pago && calcularProximoPago() && (
                    <p style={{
                      margin: "2px 0 0 0",
                      fontSize: "10px",
                      color: "#f59e0b",
                      fontWeight: "500"
                    }}>
                      Calculado
                    </p>
                  )}
                </div>

                <div style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "6px"
                  }}>
                    <TrendingUp size={14} color="#64748b" />
                    <span style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>
                      Total Pagado
                    </span>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#1e293b"
                  }}>
                    {formatearMonto(totalPagado)}
                  </p>
                </div>
              </div>
            ) : (
              <div style={{
                textAlign: "center",
                padding: "32px 20px"
              }}>
                <AlertCircle size={40} color="#64748b" style={{ marginBottom: "12px" }} />
                <h3 style={{
                  margin: "0 0 6px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#374151"
                }}>
                  Sin suscripción activa
                </h3>
                <p style={{
                  margin: 0,
                  color: "#64748b",
                  fontSize: "14px"
                }}>
                  Contacta con el administrador para activar tu suscripción.
                </p>
              </div>
            )}
          </div>

          {/* Historial de Pagos */}
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #e2e8f0"
          }}>
            <h2 style={{
              margin: "0 0 20px 0",
              fontSize: "20px",
              fontWeight: "700",
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <FileText size={20} color={colorVista.solid} />
              Historial de Pagos
            </h2>
            
            {pagos.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px"
                }}>
                  <thead>
                    <tr style={{
                      borderBottom: "1px solid #e2e8f0"
                    }}>
                      <th style={{
                        padding: "10px 12px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        Fecha
                      </th>
                      <th style={{
                        padding: "10px 12px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        Estado
                      </th>
                      <th style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        Monto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagos.slice(0, 4).map((pago, index) => (
                      <tr key={pago.id} style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: index % 2 === 0 ? "#fff" : "#f8fafc"
                      }}>
                        <td style={{
                          padding: "10px 12px",
                          color: "#374151",
                          fontSize: "13px"
                        }}>
                          {formatearFecha(pago.fecha_pago)}
                        </td>
                        <td style={{
                          padding: "10px 12px"
                        }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                          }}>
                            {pago.estado === 'approved' ? (
                              <CheckCircle size={12} color="#059669" />
                            ) : pago.estado === 'pending' ? (
                              <Clock size={12} color="#d97706" />
                            ) : (
                              <XCircle size={12} color="#dc2626" />
                            )}
                            <span style={{
                              fontSize: "12px",
                              fontWeight: "500",
                              color: pago.estado === 'approved' ? '#059669' : 
                                     pago.estado === 'pending' ? '#d97706' : '#dc2626'
                            }}>
                              {pago.estado === 'approved' ? 'Aprobado' :
                               pago.estado === 'pending' ? 'Pendiente' :
                               pago.estado === 'rejected' ? 'Rechazado' : pago.estado}
                            </span>
                          </div>
                        </td>
                        <td style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "13px"
                        }}>
                          {formatearMonto(pago.monto_bruto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pagos.length > 4 && (
                  <div style={{
                    marginTop: "12px",
                    textAlign: "center"
                  }}>
                    <p style={{
                      fontSize: "11px",
                      color: "#64748b",
                      margin: 0
                    }}>
                      Mostrando los últimos 4 pagos de {pagos.length} totales
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                textAlign: "center",
                padding: "32px 20px"
              }}>
                <FileText size={32} color="#64748b" style={{ marginBottom: "12px", opacity: 0.5 }} />
                <p style={{
                  color: "#64748b",
                  margin: 0,
                  fontSize: "14px"
                }}>
                  No hay pagos registrados
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Panel Lateral */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Información Combinada */}
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #e2e8f0"
          }}>
            <h3 style={{
              margin: "0 0 20px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <Info size={20} color={colorVista.solid} />
              Información
            </h3>
            
            {/* Detalles de Mercado Pago */}
            <div style={{ display: "grid", gap: "12px", marginBottom: "20px" }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid #f1f5f9"
              }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Plan</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                  {suscripcion?.plan || "Constructora Pro"}
                </span>
              </div>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid #f1f5f9"
              }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Moneda</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                  {suscripcion?.moneda || "ARS"}
                </span>
              </div>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid #f1f5f9"
              }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Inicio</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                  {formatearFecha(suscripcion?.fecha_inicio)}
                </span>
              </div>
              {suscripcion?.trial_days && (
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #f1f5f9"
                }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>Prueba</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#10b981" }}>
                    {suscripcion.trial_days} días
                  </span>
                </div>
              )}
              {suscripcion?.discount_code && (
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0"
                }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>Descuento</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#f59e0b" }}>
                    {suscripcion.discount_code}
                  </span>
                </div>
              )}
            </div>

            {/* Gestión de Suscripción */}
            <div style={{
              background: "#f8fafc",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #e2e8f0"
            }}>
              <p style={{
                margin: "0 0 12px 0",
                fontSize: "13px",
                color: "#374151",
                lineHeight: "1.4"
              }}>
                Para realizar cambios en tu método de pago o cancelar la suscripción, 
                debes hacerlo directamente desde tu cuenta de Mercado Pago.
              </p>
              <a
                href="https://www.mercadopago.com.ar/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: colorVista.solid,
                  textDecoration: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = "#0284c7";
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = colorVista.solid;
                }}
              >
                Gestionar en Mercado Pago
                <ExternalLink size={12} />
              </a>
            </div>
          </div>

          {/* Estadísticas Rápidas */}
          <div style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #e2e8f0"
          }}>
            <h3 style={{
              margin: "0 0 20px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <TrendingUp size={20} color={colorVista.solid} />
              Resumen
            </h3>
            <div style={{ display: "grid", gap: "12px" }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid #f1f5f9"
              }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Pagos realizados</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                  {pagos.filter(p => p.estado === 'approved').length}
                </span>
              </div>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid #f1f5f9"
              }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Pagos pendientes</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                  {pagos.filter(p => p.estado === 'pending').length}
                </span>
              </div>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0"
              }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Total pagado</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                  {formatearMonto(totalPagado)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Facturacion;
